import { Copy } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
  useShowContext,
  useTranslate,
} from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Show } from "@/components/admin/show";

import { ListCardsSkeleton } from "../design/ListStates";
import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import { formatDateOnly } from "../incentives/incentiveProgramForm";
import { isGreenFile } from "../invoicing/invoicingRules";
import { formatMxn } from "../quoter/PlanSchedule";
import { ResponsableWithContact } from "../team/ResponsableWithContact";
import type {
  AppRole,
  OperationAdvisorHistory,
  OperationSummary,
  Sale,
} from "../types";
import { CorretajeSaleAdmin } from "./CorretajeSaleAdmin";
import { OperationActions } from "./OperationActions";
import { OperationAdminForm } from "./OperationAdminForm";
import { OperationAuditHistory } from "./OperationAuditHistory";
import { OperationDocuments } from "./OperationDocuments";
import { canSeeAuditHistory } from "./operationDocumentRules";
import { SemaphoreBadge, StatusLabel } from "./OperationBadges";
import {
  canSeeClient,
  canSeeOperationDetail,
  getOperationControls,
} from "./operationRules";

const EMPTY = "—";

/**
 * Almikia Epic E4 (TASK-027): operation detail. Two separate state blocks,
 * semaforo with its reason, links to the lead and the unit, the client only
 * where the viewer can read it, the folio only once signed and a plain
 * advisor history. No delete path exists for any role.
 */
export const OperationShow = () => (
  <Show actions={false}>
    <OperationShowLayout />
  </Show>
);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(APP_LOCALE, NUMERIC_DATE_FORMAT);

const OperationShowLayout = () => {
  const { record, isPending } = useShowContext<OperationSummary>();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const notify = useNotify();

  if (isPending || !record || !identity) return <ListCardsSkeleton rows={3} />;

  const role = identity.role as AppRole;
  const isCorretaje = record.option_id != null;
  // contabilidad reads the operation and the client name only (TASK-058, QE4-8).
  const showDetail = canSeeOperationDetail(role);
  const controls = getOperationControls(
    role,
    record,
    record.sales_id === identity.id,
  );

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(record.id);
      notify("crm.operations.id_copied", { type: "success" });
    } catch {
      notify("crm.operations.error", { type: "error" });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold tabular-nums">{record.id}</h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 sm:h-8"
              onClick={copyId}
              aria-label={translate("crm.operations.actions.copy_id")}
            >
              <Copy className="size-4" aria-hidden="true" />
              {translate("crm.operations.actions.copy_id")}
            </Button>
          </div>
          <OperationActions operation={record} controls={controls} />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-1">
            <h3 className="font-semibold">
              {translate("crm.operations.fields.commercial_status")}
            </h3>
            <p data-testid="commercial-status">
              <StatusLabel kind="commercial" value={record.commercial_status} />
            </p>
            {record.cancel_reason ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.operations.fields.cancel_reason")}:{" "}
                {record.cancel_reason}
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2">
            <h3 className="font-semibold">
              {translate("crm.operations.fields.administrative_status")}
            </h3>
            <p data-testid="administrative-status">
              <StatusLabel
                kind="administrative"
                value={record.administrative_status}
              />
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <SemaphoreBadge value={record.semaphore} />
              {record.semaphore_reason ? (
                <span className="text-sm text-muted-foreground">
                  {record.semaphore_reason}
                </span>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Row label="crm.operations.fields.lead">
              <Link className="underline" to={`/deals/${record.deal_id}/show`}>
                {translate("crm.operations.open_lead")}
              </Link>
            </Row>
            {isCorretaje ? (
              <Row label="crm.corretaje.fields.option">
                <Link
                  className="underline"
                  to={`/property_options/${record.option_id}/show`}
                >
                  {record.option_address ?? EMPTY}
                </Link>
              </Row>
            ) : (
              <Row label="crm.operations.fields.unit">
                {record.project_name ?? EMPTY} ·{" "}
                {record.unit_identifier ?? EMPTY}
              </Row>
            )}
            <Row label="crm.operations.fields.advisor">
              {record.sales_id == null ? (
                (record.sales_name ?? EMPTY)
              ) : (
                <ResponsableWithContact
                  salesId={record.sales_id}
                  name={record.sales_name ?? EMPTY}
                />
              )}
            </Row>
            <Row label="crm.operations.fields.client">
              {record.client_id == null ? (
                translate("crm.operations.no_client")
              ) : canSeeClient(role) ? (
                <Link
                  className="underline"
                  to={`/clients/${record.client_id}/show`}
                >
                  {record.client_name ??
                    translate("crm.operations.open_client")}
                </Link>
              ) : record.client_name ? (
                // contabilidad / compliance: the name only, no link (they cannot open clients).
                <span data-testid="client-name">{record.client_name}</span>
              ) : (
                translate("crm.operations.client_registered")
              )}
            </Row>
            <Row label="crm.operations.fields.sale_value">
              <span className="tabular-nums">
                {record.sale_value != null
                  ? formatMxn(record.sale_value)
                  : EMPTY}
              </span>
            </Row>
            {isCorretaje ? null : (
              <>
                <Row label="crm.operations.fields.folio">
                  <span data-testid="contract-folio" className="tabular-nums">
                    {record.contract_folio ?? EMPTY}
                  </span>
                </Row>
                <Row label="crm.operations.fields.signed_on">
                  <span className="tabular-nums">
                    {record.signed_on
                      ? formatDateOnly(record.signed_on)
                      : EMPTY}
                  </span>
                </Row>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      {isCorretaje && showDetail ? (
        <CorretajeSaleAdmin operation={record} role={role} />
      ) : null}

      {showDetail ? (
        <>
          <Card>
            <CardContent className="space-y-3">
              <h3 className="font-semibold">
                {translate("crm.operations.admin_title")}
              </h3>
              <OperationAdminForm
                // Remount when the stored values change so the form re-syncs.
                key={`${record.updated_at}-${record.administrative_status}-${record.semaphore}`}
                operation={record}
                controls={controls}
                role={role}
              />
            </CardContent>
          </Card>

          <OperationDocuments
            operationId={record.id}
            role={role}
            isAdvisor={record.sales_id === identity.id}
            greenFile={isGreenFile(record)}
          />

          <AdvisorHistory operationId={record.id} />
        </>
      ) : null}

      {canSeeAuditHistory(role) ? (
        <OperationAuditHistory operationId={record.id} />
      ) : null}
    </div>
  );
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => {
  const translate = useTranslate();
  return (
    <div>
      <dt className="text-muted-foreground">{translate(label)}</dt>
      <dd>{children}</dd>
    </div>
  );
};

/** Plain chronological list: who changed, when and why. No performance data. */
const AdvisorHistory = ({ operationId }: { operationId: string }) => {
  const translate = useTranslate();
  const { data: rows, isPending } = useGetList<OperationAdvisorHistory>(
    "operation_advisor_history",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "changed_at", order: "ASC" },
      filter: { operation_id: operationId },
    },
  );
  const salesIds = [
    ...new Set(
      (rows ?? []).flatMap((row) =>
        [row.to_sales_id, row.changed_by].filter((id) => id != null),
      ),
    ),
  ];
  const { data: sales } = useGetMany<Sale>(
    "sales",
    { ids: salesIds },
    { enabled: salesIds.length > 0 },
  );
  const nameOf = (
    id: OperationAdvisorHistory["to_sales_id"] | null | undefined,
  ) => {
    const sale = (sales ?? []).find((s) => s.id === id);
    return sale ? `${sale.first_name} ${sale.last_name}` : EMPTY;
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h3 className="font-semibold">
          {translate("crm.operations.history_title")}
        </h3>
        {isPending ? (
          <ListCardsSkeleton rows={2} />
        ) : (rows ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.operations.history_empty")}
          </p>
        ) : (
          <ol className="space-y-2 text-sm" data-testid="advisor-history">
            {(rows ?? []).map((row) => (
              <li key={row.id}>
                <span className="tabular-nums">
                  {formatDate(row.changed_at)}
                </span>
                {" · "}
                {translate("crm.operations.history_entry", {
                  advisor: nameOf(row.to_sales_id),
                  author: nameOf(row.changed_by),
                })}
                {row.reason ? ` · ${row.reason}` : ""}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
